<template>
  <v-container class="px-0">
    <v-tab-item class="pb-12">
      <validation-observer v-slot="{ valid }">
        <validation-helper
          :interceptor="valid"
          @isValid="$emit('partidaTabAvailable', $event)"
        />
      
        <div class="display-1 font-weight-normal mb-6 text-center">
          Vamos começar preenchendo as informações básicas
        </div>
        <v-row class="mx-auto">
          <v-col cols="12">
            <validation-provider
              v-slot="{ errors }"
              rules="required|max:255"
            >
              <v-text-field
                id="titulo-conciliacao"
                v-model="titulo"
                label="Título"
                outlined
                :error-messages="errors"
                class="my-0"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mx-auto">
          <v-col cols="11">
            <validation-provider 
              id="flexibilidade-conciliacao"
              v-slot="{ errors }" 
              rules="required"
            >
              <v-select
                v-model="flexibilidade"
                class="my-0"
                outlined
                :items="['Flexível' ,'Rígida', 'Parcial']"
                label="Flexibilidade"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>

          <v-col
            cols="1"
            class="pl-1"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  id="dialog-flexibilidade-conciliacao"
                  v-bind="attrs"
                  size="28"
                  class="pt-4"
                  v-on="on"
                  @click.close="dialogFlexibilidade = true"
                >
                  mdi-progress-question
                </v-icon>
              </template>
              <span>Clique aqui para obter um informativo sobre as diferenças das flexibilidades</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <v-row class="mx-auto">
          <v-col cols="12">
            <v-expansion-panels
              v-model="panels"
              accordion
            >
              <v-expansion-panel id="conciliacao-editar-rodadas">
                <v-expansion-panel-header style="font-weight: 300; font-size: 15px;">
                  Editar Rodadas
                </v-expansion-panel-header>
                <v-expansion-panel-content class="expansion-panel-padding-x-clear">
                  <rodadas-conciliacoes
                    v-model="rodadas"
                    :atributos-list="atributosList"
                    :tipo-conciliacao="tipoConciliacao"
                    atributos-sm="3"
                    atributos-cols="12"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row class="mx-auto">
          <v-col
            id="conciliacao-atributos-coringa"
            cols="12"
            class="pl-2"
          >
            <div class="d-inline-flex">
              <v-checkbox 
                v-model="atributosCoringa"
                class="my-0" 
                label="Habilitar Atributos Coringa?" 
              />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    id="conciliacao-atributos-coringa-help"
                    v-bind="attrs"
                    class="mb-5 ml-1"
                    v-on="on"
                    @click="dialogAtributosCoringa = true"
                  >
                    mdi-progress-question
                  </v-icon>
                </template>
                <span>Clique aqui para obter um informativo sobre habilitar atributos coringa</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
      </validation-observer>
    </v-tab-item>

    <template v-for="(campos, key) in partidaReferencia">
      <v-tab-item
        :key="key"
        :class="`pb-12 tab-${key}-conciliacao`"
      >
        <validation-observer v-slot="{ valid }">
          <validation-helper 
            :interceptor="valid" 
            @isValid="$emit(key == 'partida' ? 'referenciaTabAvailable' : 'estimativaTempoTabAvailable', $event)" 
          />
        
          <div :id="`conciliacao-base-de-${key}`">
            <v-row
              align="center"
              class="mx-auto"
            >
              <v-col cols="12">
                <validation-provider 
                  :id="`selecionar-origem-${key}-conciliacao`"
                  v-slot="{ errors }" 
                  rules="required"
                >
                  <v-select
                    v-model="campos.origem"
                    outlined
                    :items="['Arquivo', 'Consulta']"
                    :label="key == 'partida' ? 'Origem Base de Partida' : 'Origem Base de Referência'"
                    :error-messages="errors"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row
              align="center"
              class="mx-auto"
            >
              <v-col
                v-show="campos.origem"
                cols="12"
                class="mb-4"
              >
                <v-alert
                  dense
                  outlined
                  type="info"
                  color="blue"
                  class="pa-2"
                >
                  <div v-if="campos.origem == 'Consulta'">
                    A consulta selecionada deve conter os campos <b>ID</b> e <b>QTD</b>.
                    <br>Obs: O campo <b>QTD</b> pode conter os nome <b>QTD Somatório</b>, <b>QTD_MCPSE</b> ou <b>QTD_MCPSE Somatório</b> ou <b>QTD</b> para ser validado.
                    <br>Obs²: O campo <b>ID</b> pode conter os nomes <b>ID</b>, <b>COD_ID</b>, <b>IMOBILIZADO</b> ou <b>SUBNÚMERO</b> para ser validado.
                  </div>

                  <div v-if="campos.origem == 'Arquivo'">
                    O arquivo selecionado deve conter os campos <b>ID</b> e <b>QTD</b>.
                    <br>Obs: O campo <b>QTD</b> pode conter os nome <b>QTD Somatório</b>, <b>QTD_MCPSE</b>, <b>QTD_MCPSE Somatório</b> ou <b>QTD</b> para ser validado.
                    <br>Obs²: O campo <b>ID</b> pode conter os nomes <b>ID</b>, <b>COD_ID</b>, <b>IMOBILIZADO</b>, <b>EI</b> ou <b>SUBNÚMERO</b> para ser validado.
                    <br><a
                      href="javascript:void(0)"
                      @click="getArquivoModelo()"
                    >Clique aqui</a> para baixar o arquivo modelo.
                  </div>
                </v-alert>
              </v-col>
            </v-row>

            <v-row
              v-if="campos.origem == 'Consulta'"
              align="center"
              class="mx-auto"
            >
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    :id="`selecionar-consulta-${key}-conciliacao`"
                    v-model="campos.consultaNome"
                    label="Consulta"
                    placeholder="Selecione uma consulta"
                    readonly 
                    outlined
                    :success="campos.headerValido"
                    :error-messages="errors"
                    @focus.close="relatoriosDialog = true"
                  />
                </validation-provider>
              </v-col>

              <validation-provider rules="required">
                <input
                  type="hidden"
                  :name="`valida-header-consulta-${key}`"
                  :value="campos.headerValido ? 'OK' : ''"
                >
              </validation-provider>
            </v-row>

            <v-row
              v-if="campos.origem == 'Arquivo'"
              align="center"
              class="mx-auto"
            >
              <v-col
                cols="12"
                class="pl-2"
              >
                <validation-provider 
                  :id="`selecionar-arquivo-${key}-conciliacao`"
                  v-slot="{ errors }" 
                  rules="required"
                >
                  <v-file-input
                    v-model="campos.arquivo"
                    label="Arquivo CSV"
                    placeholder="Selecione um arquivo CSV"
                    outlined
                    accept=".csv, .zip"
                    :show-size="!campos.fakeFile"
                    truncate-length="30"
                    :loading="campos.uploading"
                    loader-height="4"
                    :success="(campos.arquivoId && campos.arquivo && campos.headerValido) ? true : false"
                    :error-messages="errors"
                    @change="uploadNewFile(campos)"
                  />
                </validation-provider>

                <validation-provider rules="required">
                  <input
                    type="hidden"
                    :name="`valida-upload-${key}`"
                    :value="(campos.arquivoId && campos.headerValido) ? 'OK' : ''"
                  >
                </validation-provider>
              </v-col>
            </v-row>

            <parametros-arquivo-csv 
              v-if="campos.origem == 'Arquivo'"
              :id="`campos-arquivo-${key}-conciliacao`"
              :delimitador.sync="campos.delimitador"
              :involucro.sync="campos.involucro"
              :escape.sync="campos.escape"
            />
          </div>

          <v-row
            align="center"
            class="mx-auto"
          >
            <v-col cols="12">
              <v-expansion-panels
                v-model="campos.panels"
                accordion
              >
                <v-expansion-panel :id="`conciliacao-editar-mapeamento-${key}`">
                  <v-expansion-panel-header style="font-weight: 300; font-size: 15px;">
                    Editar Mapeamento
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <tbody>
                          <tr
                            v-for="(value, coluna) in campos.mapeamento"
                            :key="coluna"
                          >
                            <td>
                              <strong class="d-inline-flex">{{ coluna | replaceUnderscoresWithSpace }}
                                <v-icon
                                  v-if="atributosObrigatorios.includes(coluna)"
                                  size="9"
                                  class="mb-1 pl-1"
                                  color="error"
                                >
                                  mdi-asterisk
                                </v-icon>
                              </strong>
                            </td>
                            <td style="max-width: 125px;"> 
                              <validation-provider 
                                v-slot="{ errors }"
                                :rules="atributosObrigatorios.includes(coluna) ? 'required' : undefined" 
                              >
                                <v-select
                                  v-model="campos.mapeamento[coluna]"
                                  dense
                                  :items="campos.header"
                                  clearable
                                  no-data-text="Nenhum campo disponível."
                                  :hide-details="errors.length ? false : true"
                                  :error="atributosObrigatorios.includes(coluna) && !campos.mapeamento[coluna]"
                                  :error-messages="errors"
                                />
                              </validation-provider>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </validation-observer>
      </v-tab-item>
    </template>

    <v-tab-item class="pb-12">
      <tempo-estimado-conciliacao-alert
        :tempo-estimado="tempoEstimado"
        :loading="loading"
      />
    </v-tab-item>

    <general-relatorios-selector
      :activated.sync="relatoriosDialog"
      @selected="setRelatorio($event)"
    />
    <dialog-flexibilidade :activated.sync="dialogFlexibilidade" />
    <dialog-atributos-coringa :activated.sync="dialogAtributosCoringa" />
  </v-container>
</template>

<script>

import ConciliacoesService from '@/services/ConciliacoesService.js';
import conciliacoesWizardTabsMixins from '@/mixins/conciliacoes/conciliacoesWizardTabsMixins.js';
import randomString from '@/utils/generateRandomString';

export default {
  components: {
    ValidationHelper: () => import('@/components/general/ValidationHelper.vue'),
    GeneralRelatoriosSelector: () => import('@/components/general/selectors/GeneralRelatoriosSelector.vue'),
    RodadasConciliacoes: () => import('@/components/conciliacoes/RodadasConciliacoes.vue'),
    ParametrosArquivoCsv: () => import('@/components/general/GeneralParametrosArquivoCsv.vue'),
    DialogFlexibilidade: () => import('@/components/conciliacoes/DialogFlexibilidade.vue'),
    DialogAtributosCoringa: () => import('@/components/conciliacoes/DialogAtributosCoringa.vue'),
    TempoEstimadoConciliacaoAlert: () => import('@/components/conciliacoes/TempoEstimadoConciliacaoAlert.vue')
  },

  mixins: [conciliacoesWizardTabsMixins],

  data() {
    return {
      atributosList: ['TUC', 'ODI', 'TI', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'UAR', 'IDUC', 'Inventario', 'Obra', 'Material', 'Data', 'Generico_1', 'Generico_2', 'Generico_3'],
      prioridades: ['TUC'],
      tipoConciliacao: 'Avançada',
      panels: 0,
      precisaOrganizarPrioridades: false
    };
  },

  computed: {
    atributosObrigatorios() {
      let itensObrigatorios = ['ID', 'QTD', ...this.prioridades];

      if (this.tab === 2 && this.prioridades.includes('Data')) {
        itensObrigatorios.push('Data_Conexao');
      }

      return itensObrigatorios;
    }
  },

  watch: {
    conciliacao() {
      this.fillFields();
    },
    rodadas() {
      this.precisaOrganizarPrioridades = true;
    },
    tab(newTab) {
      if (newTab !== 0 && this.precisaOrganizarPrioridades) {
        this.organizarPrioridades();
      }

      if (newTab === 3) {
        this.getTempoEstimado();
      }
    }
  },

  mounted() {
    if(!this.$route.params.id) { 
      this.getCsvCamposPadroes();
    } else {
      this.fillFields();
    }
  },

  methods: {
    getDefaultPartidaReferencia(tipo) {
      return {
        origem: 'Arquivo',
        consultaId: '',
        consultaNome: '',
        arquivo: undefined,
        header: [],
        headerValido: false,
        uploading: false,
        arquivoId: '',
        delimitador: ';',
        involucro: '"',
        escape: '"',
        fakeFile: false,
        panels: undefined,
        mapeamento: this.getDefaultMapeamento(tipo)
      };
    },
    getDefaultMapeamento(tipo) {
      let mapeamento =  {
        'ID': '',
        'QTD': '',
        'TUC': '', 
        'ODI': '', 
        'TI': '', 
        'A1': '', 
        'A2': '', 
        'A3': '', 
        'A4': '', 
        'A5': '', 
        'A6': '', 
        'UAR': '', 
        'IDUC': '', 
        'Inventario': '', 
        'Obra': '', 
        'Material': '', 
        'Data': '', 
        'Generico_1': '', 
        'Generico_2': '', 
        'Generico_3': ''
      };

      // Campo Data_Conexao é exclusivo da base de Referência.
      if (tipo === 'referência') {
        mapeamento['Data_Conexao'] = '';
      }

      return mapeamento;
    },
    validarHeader(id, campos) {
      const base = this.tab == 1 ? 'partida' : 'referência';
      const postData = {
        tipoConciliacao: this.tipoConciliacao,
        origem: campos.origem,
        delimitador: campos.delimitador,
        involucro: campos.involucro,
        escape: campos.escape,
        base: base
      };

      ConciliacoesService.validaHeader(id, postData)
        .then(res => {
          campos.mapeamento = this.getDefaultMapeamento(base);
          campos.header = res.data.header;
          campos.headerValido = true;
          campos.panels = 0;
          
          campos.header.forEach(col => {
            if(campos.mapeamento[col] != undefined) campos.mapeamento[col] = col;
          });
        }).catch(err => {
          let msg = '';
          if(err && err.data && err.data.erro_1) {
            msg = err.data.erro_1;
            if(campos.origem == 'Arquivo' && err.data.erro_2) msg += `<br>${err.data.erro_2}`;
          } else {
            msg = `Erro ao verificar colunas do(a) ${campos.origem}.`;
          }

          this.$toast.error(msg, '', { position: 'topRight', timeout: 5000 });
          console.error(err);

          campos.headerValido = false;
          campos.arquivo = undefined;
          campos.arquivoId = '';
          campos.consultaNome = '';
          campos.consultaId = '';
        });
    },
    fillFields() {
      const item = this.conciliacao;
      item.parametros = JSON.parse(item.parametros);
      
      this.titulo = item.nome;
      this.flexibilidade = item.flexibilidade;
      
      this.rodadas = item.parametros?.rodadas 
        ? item.parametros.rodadas.map(rodada => { return { key: randomString(20), valores: rodada }; })
        : [{ key: randomString(20), valores: ['TUC'] }];

      this.prioridades = item.parametros?.prioridades ? item.parametros.prioridades : [];
      this.atributosCoringa = item.parametros?.vazio == 'DIFNAN' ? true : false;

      this.partidaReferencia = {
        partida: {
          origem: item.origem_dados_contabil,
          consultaId: item.consulta_contabil_id,
          consultaNome: item.origem_dados_contabil == 'Consulta' && item.consulta_contabil ? item.consulta_contabil.nome : '',
          arquivo: item.origem_dados_contabil == 'Arquivo' && item.arquivo_contabil ? this.getFakeFileForValidation(item.arquivo_contabil.arquivo) : undefined,
          arquivoId: item.arquivo_contabil_id,
          uploading: false,
          delimitador: item.arquivo_contabil_delimitador,
          involucro: item.arquivo_contabil_involucro,
          escape: item.arquivo_contabil_escape,
          header: item.header_contabil,
          headerValido: true,
          fakeFile: item.origem_dados_contabil == 'Arquivo' ? true : false,
          panels: undefined,
          mapeamento: JSON.parse(item.mapeamento_contabil)
        },
        referencia: {
          origem: item.origem_dados_fisico,
          consultaId: item.consulta_fisico_id,
          consultaNome: item.origem_dados_fisico == 'Consulta' && item.consulta_fisico ? item.consulta_fisico.nome : '',
          arquivo: item.origem_dados_fisico == 'Arquivo' && item.arquivo_fisico ? this.getFakeFileForValidation(item.arquivo_fisico.arquivo) : undefined,
          arquivoId: item.arquivo_fisico_id,
          uploading: false,
          delimitador: item.arquivo_fisico_delimitador,
          involucro: item.arquivo_fisico_involucro,
          escape: item.arquivo_fisico_escape,
          header: item.header_fisico,
          headerValido: true,
          fakeFile: item.origem_dados_fisico == 'Arquivo' ? true : false,
          panels: undefined,
          mapeamento: JSON.parse(item.mapeamento_fisico)
        }
      };
    },
    validaMapeamento(objMapeamento) {
      for (const atrib of ['ID', 'QTD', ...this.prioridades]) {
        if (!objMapeamento[atrib]) return false;
      }
      return true;
    },
    organizarPrioridades() {
      if (this.precisaOrganizarPrioridades) {
        let atributos = [];
        this.rodadas.forEach(arr => atributos = atributos.concat(atributos, arr.valores));
        this.prioridades = Array.from(new Set(atributos));
      }

      this.precisaOrganizarPrioridades = false;
    }
  }
};
</script>

<style>
  .ghost {
    opacity: 0.5;
  }
</style>